import { isPlatformBrowser, UpperCasePipe } from '@angular/common';
import type {
  AfterViewInit,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  TemplateRef,
} from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  inject,
  Input,
  Output,
  PLATFORM_ID,
  ViewChild,
} from '@angular/core';
import type { AbstractControl } from '@angular/forms';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import {
  DateRange as MatDateRange,
  MatCalendar,
  MatDatepicker,
  MatDatepickerModule,
} from '@angular/material/datepicker';
import { shakeAnimation } from '@freelancer/animations';
import { RepetitiveSubscription } from '@freelancer/decorators';
import { FreelancerAsyncPipeModule } from '@freelancer/pipes';
import type { Timer } from '@freelancer/time-utils';
import { TimeUtils } from '@freelancer/time-utils';
import { ButtonComponent } from '@freelancer/ui/button';
import { ButtonColor, ButtonSize } from '@freelancer/ui/button';
import { Focus } from '@freelancer/ui/focus';
import { IconModule } from '@freelancer/ui/icon';
import { HoverColor, IconColor, IconSize } from '@freelancer/ui/icon';
import { FontWeight } from '@freelancer/ui/text';
import { ValidationErrorComponent } from '@freelancer/ui/validation-error';
import { dirtyAndValidate } from '@freelancer/ui/validators';
import type { IsAny } from '@freelancer/utils';
import { isDefined, isString } from '@freelancer/utils';
import { BehaviorSubject, combineLatest, Subscription } from 'rxjs';
import { filter, startWith } from 'rxjs/operators';
import { NumberValueAccessorDirective } from './value-accessors/number-value-accessor.directive';
import { generateUniqueID } from '../helpers/helpers';
import { LocalizedDateFns } from '../localized-date-fns.service';
import { TESTING_ANIMATION_DISABLED } from '../ui.config';
import {
  AutoCompleteHint,
  AutoFocusOption,
  ControlType,
  DateRange,
  InputAlign,
  InputBackgroundColor,
  InputEditInlineTitleSize,
  InputEditInlineType,
  InputIconEnd,
  InputIconStart,
  InputSize,
  InputTextAlign,
  InputTextColor,
  InputType,
  KeyboardType,
} from './input.types';
import { SearchExpandFromDirection } from '../search/search.types';

@Component({
  standalone: true,
  selector: 'fl-input',
  imports: [
    ButtonComponent,
    IconModule,
    MatDatepickerModule,
    NumberValueAccessorDirective,
    ReactiveFormsModule,
    ValidationErrorComponent,
    UpperCasePipe,
    FreelancerAsyncPipeModule,
  ],
  template: `
    @if (type !== InputType.DATERANGE_INLINE) {
      <div
        class="InputInner"
        [attr.data-type]="type"
        [attr.data-align]="align"
        [attr.data-edit-inline]="editInline"
        [attr.data-background-color]="backgroundColor"
      >
        @if (isNumberIncrement) {
          <fl-button
            i18n="Input increment button"
            class="InputIncrement"
            [color]="ButtonColor.CUSTOM"
            [disabled]="
              attrDisabled === true ||
              attrReadonly === true ||
              shouldDisableDecrementButton()
            "
            [size]="buttonIncrementSize"
            [attr.data-invalid]="hasError && !isWarning"
            [attr.data-valid]="isValid"
            [attr.data-warning]="isWarning"
            (click)="handleMinus()"
          >
            <p>-</p>
          </fl-button>
        }
        <div
          #inputContainer
          class="InputContainer"
          [class.BeforeLabelPresent]="beforeLabel"
          [class.AfterLabelPresent]="afterLabel"
          [class.Shadowed]="isShadowed"
          [class.IsFocused]="inputFocused === true"
          [class.IsExpandable]="isExpandable === true"
          [class.ExpandFromRight]="
            expandFromDirection === SearchExpandFromDirection.RIGHT
          "
          [class.IsBorderless]="borderless === true"
          [class.IsBorderlessMobile]="borderlessMobile === true"
          [class.SearchInput]="type === InputType.SEARCH"
          [class.SearchInput-roundCorners]="
            type === InputType.SEARCH && searchRoundCorners
          "
          [class.IsInvalid]="hasError && !isWarning"
          [class.IsValid]="isValid === true"
          [class.IsDateRangeDataApplied]="
            this.type === InputType.DATERANGE &&
            showDateSelectedState &&
            !!control.value &&
            isDateRangeControl(control) &&
            !!control.value?.start &&
            !!control.value?.end
          "
          [class.CenterAlign]="shouldCenterAlign"
          [attr.data-size]="size"
          [attr.data-size-tablet]="sizeTablet"
          [attr.data-size-desktop]="sizeDesktop"
          [attr.disabled]="attrDisabled"
          [attr.data-expanded]="expanded"
          [attr.data-edit-inline]="editInline"
          [attr.data-hide-icon]="
            inputFocused ||
            (isString(control.value) && control.value?.length > 0)
          "
          [attr.data-transparent-background]="transparentBackground"
          [@shakeAnimation]="{
            value: shakeState,
            params: {
              duration: 200,
              translateX: '3px',
            },
          }"
          (@shakeAnimation.done)="shakeDone()"
        >
          @if (beforeLabel) {
            <div
              class="InputLabel BeforeLabel"
              [attr.data-size]="size"
            >
              <span class="LabelText"> {{ beforeLabel }} </span>
            </div>
          }
          @if (iconStartClickable) {
            <fl-button
              [class]="
                iconMarginMobile
                  ? 'InputButtonStart'
                  : 'InputButtonStart-noMarginMobile'
              "
              [label]="iconStartLabel"
              (click)="handleIconStartClick($event)"
            >
              @if (iconStart) {
                <fl-icon
                  [size]="leftIconSize"
                  [color]="leftIconColor"
                  [hoverColor]="HoverColor.PRIMARY"
                  [name]="iconStart"
                  [useIconFont]="true"
                  [fillIconFont]="true"
                ></fl-icon>
              }
            </fl-button>
          }
          @if (iconStart && !iconStartClickable) {
            <fl-icon
              [class]="
                iconMarginMobile
                  ? 'IconElementStart'
                  : 'IconElementStart-noMarginMobile'
              "
              [size]="leftIconSize"
              [color]="leftIconColor"
              [label]="iconStartLabel"
              [name]="iconStart"
              [useIconFont]="true"
              [fillIconFont]="true"
            ></fl-icon>
          }
          <div
            class="NativeElementContainer"
            [class.DateRangeNativeElementContainer]="
              type === InputType.DATERANGE
            "
            [attr.data-background-color]="backgroundColor"
            [attr.data-transparent-background]="transparentBackground"
            [attr.data-edit-inline]="editInline"
            [attr.disabled]="attrDisabled"
          >
            @if (isDateInput) {
              <!--
              NOTE: for [type="date"] we set the native [attr.type] to "text"
              This is to avoid the native date-picker stuff showing up
              -->
              @if (type === InputType.DATERANGE) {
                <mat-date-range-input
                  [formGroup]="dateRangeGroup"
                  [max]="maxDate"
                  [min]="minDate"
                  [rangePicker]="picker"
                >
                  <input
                    #nativeElement
                    matStartDate
                    class="NativeElement DateRangeNativeElementLeft"
                    [attr.id]="id"
                    [attr.type]="'text'"
                    [attr.placeholder]="
                      editInline !== InputEditInlineType.BORDERED ||
                      inputFocused
                        ? (dateFormat | flAsync | uppercase)
                        : ''
                    "
                    [attr.readonly]="attrReadonly ? true : null"
                    [attr.disabled]="attrDisabled"
                    [attr.maxlength]="maxLength ? maxLength : null"
                    [attr.data-align]="textAlign"
                    [attr.data-text-color]="color"
                    [attr.aria-label]="label ?? 'Start Date'"
                    [attr.autocomplete]="autoComplete"
                    [attr.data-size]="size"
                    [attr.data-borderless]="borderless"
                    [attr.data-borderlessMobile]="borderlessMobile"
                    [attr.data-weight]="fontWeight"
                    formControlName="dateStartControl"
                    (focus)="handleFocusDatePicker('nativeElement')"
                    (keydown)="handleKeydown()"
                    (blur)="handleBlurDatePicker()"
                  />
                  <input
                    #nativeElement2
                    matEndDate
                    class="NativeElement DateRangeNativeElementRight"
                    [attr.type]="'text'"
                    [attr.placeholder]="
                      editInline !== InputEditInlineType.BORDERED ||
                      inputFocused
                        ? (dateFormat | flAsync | uppercase)
                        : ''
                    "
                    [attr.readonly]="attrReadonly ? true : null"
                    [attr.disabled]="attrDisabled"
                    [attr.maxlength]="maxLength ? maxLength : null"
                    [attr.data-align]="textAlign"
                    [attr.data-text-color]="color"
                    [attr.aria-label]="label ?? 'End Date'"
                    [attr.autocomplete]="autoComplete"
                    [attr.data-size]="size"
                    [attr.data-borderless]="borderless"
                    [attr.data-borderlessMobile]="borderlessMobile"
                    [attr.data-weight]="fontWeight"
                    formControlName="dateEndControl"
                    (focus)="handleFocusDatePicker('nativeElement2')"
                    (keydown)="handleKeydown()"
                    (blur)="handleBlurDatePicker()"
                  />
                </mat-date-range-input>
                <mat-date-range-picker #picker></mat-date-range-picker>
              } @else {
                <input
                  #nativeElement
                  class="NativeElement"
                  [class.HasIconStart]="iconStart"
                  [class.HasIconEnd]="iconEnd"
                  [class.CompactDateInput]="compactDateInput"
                  [attr.id]="id"
                  [attr.type]="'text'"
                  [attr.placeholder]="
                    editInline !== InputEditInlineType.BORDERED || inputFocused
                      ? (dateFormat | flAsync | uppercase)
                      : ''
                  "
                  [attr.readonly]="attrReadonly ? true : null"
                  [attr.disabled]="attrDisabled"
                  [attr.maxlength]="maxLength ? maxLength : null"
                  [attr.data-align]="textAlign"
                  [attr.data-text-color]="color"
                  [attr.data-applied]="showDateSelectedState && !!control.value"
                  [attr.aria-label]="label"
                  [attr.autocomplete]="autoComplete"
                  [attr.data-size]="size"
                  [attr.data-borderless]="borderless"
                  [attr.data-borderlessMobile]="borderlessMobile"
                  [attr.data-transparent-background]="transparentBackground"
                  [attr.data-weight]="fontWeight"
                  [formControl]="control"
                  [matDatepicker]="picker"
                  [max]="maxDate"
                  [min]="minDate"
                  (focus)="handleFocusDatePicker('nativeElement')"
                  (keydown)="handleKeydown()"
                  (blur)="handleBlurDatePicker()"
                />
                <mat-datepicker #picker></mat-datepicker>
              }
            } @else {
              <!--
              This is separate because otherwise matDatepicker will add date validation
              -->
              @if (isNumberInput) {
                <!--
                FIXME: T276037 - This is separate because [attr.type] binding with 'number'
                returns 'string' instead of 'number': https://github.com/angular/angular/issues/13243
                -->
                <input
                  #nativeElement
                  class="NativeElement"
                  [class.HasIconStart]="iconStart"
                  [class.HasIconEnd]="iconEnd"
                  type="number"
                  [attr.controlType]="controlType"
                  [attr.id]="id"
                  [attr.step]="type === InputType.CURRENCY ? 0.01 : null"
                  [attr.placeholder]="
                    editInline !== InputEditInlineType.BORDERED || inputFocused
                      ? placeholder
                      : ''
                  "
                  [attr.readonly]="attrReadonly ? true : null"
                  [attr.disabled]="attrDisabled"
                  [attr.maxlength]="maxLength ? maxLength : null"
                  [attr.data-align]="textAlign"
                  [attr.data-text-color]="color"
                  [attr.autocomplete]="autoComplete"
                  [attr.data-size]="size"
                  [attr.data-left-icon-size]="leftIconSize"
                  [attr.data-right-icon-size]="rightIconSize"
                  [attr.aria-errormessage]="errorId"
                  [attr.aria-invalid]="hasError && !isWarning"
                  [attr.aria-label]="label"
                  [attr.data-borderless]="borderless"
                  [attr.data-borderlessMobile]="borderlessMobile"
                  [attr.data-custom-arrows]="
                    isNumberIncrement || type === InputType.NUMBER_TEXT
                  "
                  [attr.data-edit-inline]="editInline"
                  [attr.min]="minValue"
                  [attr.data-transparent-background]="transparentBackground"
                  [attr.inputmode]="
                    type === InputType.CURRENCY ? 'decimal' : keyboardType
                  "
                  [formControl]="control"
                  (focus)="handleFocus()"
                  (blur)="handleBlur()"
                  (keyup)="handleKeyUp()"
                  (wheel)="handleInputWheel()"
                  (change)="handleOnChange()"
                />
              } @else {
                <input
                  #nativeElement
                  class="NativeElement"
                  [class.HasIconStart]="iconStart"
                  [class.HasIconEnd]="iconEnd"
                  [attr.id]="id"
                  [attr.type]="inputType"
                  [attr.step]="type === InputType.CURRENCY ? 0.01 : null"
                  [attr.placeholder]="
                    editInline !== InputEditInlineType.BORDERED || inputFocused
                      ? getPlaceholderWithCursor()
                      : ''
                  "
                  [attr.readonly]="attrReadonly ? true : null"
                  [attr.disabled]="attrDisabled"
                  [attr.maxlength]="maxLength ? maxLength : null"
                  [attr.data-align]="textAlign"
                  [attr.data-text-color]="color"
                  [attr.autocomplete]="autoComplete"
                  [attr.data-size]="size"
                  [attr.data-size-tablet]="sizeTablet"
                  [attr.data-size-desktop]="sizeDesktop"
                  [attr.data-left-icon-size]="leftIconSize"
                  [attr.data-right-icon-size]="rightIconSize"
                  [attr.aria-errormessage]="errorId"
                  [attr.aria-invalid]="hasError && !isWarning"
                  [attr.aria-label]="label"
                  [attr.data-borderless]="borderless"
                  [attr.data-borderlessMobile]="borderlessMobile"
                  [attr.data-weight]="fontWeight"
                  [attr.data-edit-inline-title-size]="
                    isText && editInlineTitleSize ? editInlineTitleSize : null
                  "
                  [attr.data-is-shadowed]="isShadowed"
                  [attr.min]="minValue"
                  [formControl]="control"
                  [attr.data-background-color]="backgroundColor"
                  [attr.data-transparent-background]="transparentBackground"
                  [attr.data-edit-inline]="editInline"
                  [attr.inputmode]="keyboardType"
                  (focus)="handleFocus()"
                  (blur)="handleBlur()"
                  (keyup)="handleKeyUp()"
                  (wheel)="handleInputWheel()"
                  (change)="handleOnChange()"
                />
              }
            }
          </div>
          @if (iconEndClickable) {
            <fl-button
              class="InputButtonEnd"
              [label]="iconEndLabel"
              [class.mobile:hide]="compactDateInput"
              (click)="handleIconEndClick($event)"
            >
              @if (iconEnd) {
                <fl-icon
                  [size]="rightIconSize"
                  [color]="rightIconColor"
                  [hoverColor]="HoverColor.PRIMARY"
                  [name]="iconEnd"
                  [useIconFont]="true"
                  [fillIconFont]="true"
                ></fl-icon>
              }
            </fl-button>
          }
          @if (iconEnd && !iconEndClickable) {
            <fl-icon
              class="IconElementEnd"
              [size]="rightIconSize"
              [color]="rightIconColor"
              [label]="iconEndLabel"
              [name]="iconEnd"
              [useIconFont]="true"
              [fillIconFont]="true"
            ></fl-icon>
          }
          @if (afterLabel) {
            <div
              class="InputLabel AfterLabel"
              [attr.data-size]="size"
            >
              <span class="LabelText"> {{ afterLabel }} </span>
            </div>
          }
          @if (
            isNumberInput &&
            showIncrement &&
            (type === InputType.NUMBER_TEXT || type === InputType.NUMBER)
          ) {
            <div class="InputIncrementContainer mr-xxsmall">
              <fl-icon
                class="InputIncrementContainer-button"
                [label]="'Increment'"
                [name]="'ui-chevron-up'"
                [size]="IconSize.SMALL"
                [useIconFont]="true"
                [fillIconFont]="true"
                [weight]="FontWeight.MEDIUM"
                (click)="handleAdd()"
              ></fl-icon>
              <fl-icon
                class="InputIncrementContainer-button Decrement"
                [name]="'ui-chevron-down'"
                [label]="'Decrement'"
                [size]="IconSize.SMALL"
                [useIconFont]="true"
                [fillIconFont]="true"
                [weight]="FontWeight.MEDIUM"
                (click)="handleMinus()"
              ></fl-icon>
            </div>
          }
          <ng-content select="fl-button"></ng-content>
        </div>
        @if (editInline) {
          <label
            class="LabelTag"
            for="id"
            [class.HasValue]="
              (isStringControl(control) && control.value.length !== 0) ||
              (isNumberControl(control) && isDefined(control.value))
            "
            [class.BeforeLabelSpace]="beforeLabel"
            [class.HideLabelTag]="hideLabelTag"
            [class.TitleInput]="isText && editInlineTitleSize"
            [class.IsInvalid]="hasError && !isWarning"
            [class.IsValid]="isValid === true"
            [attr.data-edit-inline-title-size]="
              isText && editInlineTitleSize ? editInlineTitleSize : null
            "
            [attr.data-background-color]="backgroundColor"
          >
            {{ labelTag }}
          </label>
        }
        @if (maxCharacter !== undefined && !hasError) {
          <p
            class="Counter text-body-16 {{
              remainingCharCount >= 0 ? 'text-foreground' : 'text-error'
            }}"
            [class.IsFocus]="inputFocused"
          >
            @if (remainingCharCount >= 0) {
              <ng-container i18n="Input field characters left text">
                {{ remainingCharCount }} characters left
              </ng-container>
            }
            @if (remainingCharCount < 0) {
              <ng-container i18n="Input field characters over limit text">
                {{ -remainingCharCount }} characters over limit
              </ng-container>
            }
          </p>
        }
        @if (isNumberIncrement) {
          <fl-button
            i18n="Input increment button"
            class="InputIncrement {{ hasError ? 'mb-xxxsmall' : 'mb-none' }}"
            [color]="ButtonColor.CUSTOM"
            [disabled]="attrDisabled === true || attrReadonly === true"
            [size]="buttonIncrementSize"
            [attr.data-invalid]="hasError && !isWarning"
            [attr.data-valid]="isValid"
            [attr.data-warning]="isWarning"
            (click)="handleAdd()"
          >
            <p>+</p>
          </fl-button>
        }
      </div>
    } @else {
      <mat-calendar
        ngSkipHydration
        [selected]="inlineDateRangeValue"
        (selectedChange)="handleInlineDateRangeChange($event)"
        #inlineRangePicker
      >
      </mat-calendar>
    }
    @if (hint && inputFocused && !isErrorDisplayed && !isValid) {
      <p class="Hint">
        {{ hint }}
      </p>
    }
    @if (displayValidation) {
      <fl-validation-error
        class="ValidationError"
        [id]="errorId"
        [attr.data-align]="align"
        [control]="control"
        [isWarning]="isWarning"
        [showIcon]="false"
        [customValidationError]="customValidationError"
      >
      </fl-validation-error>
    }
  `,
  styleUrls: ['./input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [shakeAnimation],
})
export class InputComponent<T>
  implements AfterViewInit, OnChanges, OnInit, OnDestroy
{
  private cd = inject(ChangeDetectorRef);
  private datefns = inject(LocalizedDateFns);
  private focus = inject(Focus);
  private timeUtils = inject(TimeUtils);
  private platformId = inject(PLATFORM_ID);
  private readonly testingDatepickerOpenCloseDisabled = inject(
    TESTING_ANIMATION_DISABLED,
    { optional: true },
  );

  ButtonColor = ButtonColor;
  HoverColor = HoverColor;
  IconSize = IconSize;
  InputType = InputType;
  FontWeight = FontWeight;
  InputEditInlineType = InputEditInlineType;
  SearchExpandFromDirection = SearchExpandFromDirection;

  isDefined = isDefined;
  isString = isString;

  iconEndClickable = false;
  iconStartClickable = false;
  inputFocused = false;
  isErrorDisplayed = false;
  isValid = false;
  isWarning = false;
  remainingCharCount = 0;
  showPassword = false;

  @RepetitiveSubscription()
  private errorSubscription?: Subscription;
  private autofocusSubscription?: Subscription;
  private dateRangeSubscription?: Subscription;
  private dateRangeControlSubscription?: Subscription;

  attrDisabled?: true;
  buttonIncrementSize: ButtonSize;
  dateFormat: Promise<string>;
  errorId: string;
  isDateInput: boolean;
  isPasswordInput: boolean;

  PLACEHOLDER_ANIMATION_FULL_TEXT_DELAY = 1000;
  PLACEHOLDER_ANIMATION_TYPING_DELAY = 150;

  currentAnimatedPlaceholderIndex = 0;
  private placeholderAnimationTimeout?: Timer;

  // Only used for date range
  dateRangeGroup: FormGroup<{
    dateStartControl: FormControl<Date | undefined>;
    dateEndControl: FormControl<Date | undefined>;
  }>;

  // Only used for inline date range
  inlineDateRangeValue?: DateRange;

  @Input() editInline: InputEditInlineType;
  @Input() expanded = false;
  @Input() hideLabelTag = false;
  @Input() id?: string;
  @Input() attrReadonly?: true;
  @Input() control: FormControl<T>;
  @Input() displayValidation = true;

  // If provided, the validation error will display the custom error template instead of the default error text
  @Input() customValidationError?: TemplateRef<HTMLElement>;
  /**
   * Not for general use. This is only for larger title inputs of type TEXT.
   * If you'd like to use this input, make sure you discuss with UI Eng
   */
  @Input() editInlineTitleSize?: InputEditInlineTitleSize;
  @Input() fontWeight: FontWeight = FontWeight.NORMAL;
  @Input() placeholder?: string;
  @Input() labelTag?: string;
  @Input() @HostBinding('attr.data-size') size: InputSize = InputSize.MID;
  @Input() sizeTablet: InputSize;
  @Input() sizeDesktop: InputSize;

  @Input() transparentBackground = false;

  @Input() isShadowed = false;

  /**
   * Not for general use. This is only for search.
   * If you'd like to use this input, make sure you discuss with UI Eng
   */
  @Input() isExpandable = false;
  /**
   * Not for general use. This is only for search.
   * If you'd like to use this input, make sure you discuss with UI Eng
   */
  @Input() expandFromDirection: SearchExpandFromDirection =
    SearchExpandFromDirection.RIGHT;
  /**
   * Not for general use. This is only for Messaging search.
   * If you'd like to use this input, make sure you discuss with UI Eng
   */
  @Input() borderless = false;
  /**
   * Not for general use. This is only for browse mobile page.
   * If you'd like to use this input, make sure you discuss with UI Eng
   */
  @Input() borderlessMobile = false;
  /**
   * Not for general use. This is only for browse mobile page.
   * If you'd like to use this input, make sure you discuss with UI Eng.
   */
  @Input() iconMarginMobile = true;
  /**
   * Clears the inline date range picker's value. Only used for InputType.DATERANGE_INLINE.
   * Since inline usage of sat-calendar component doesn't have a binding input element,
   * we need to reset the date range picker's value manually when the form control's value gets reseted.
   */
  @Input() clearDateRange = false;
  // FIXME: T241170 - Remove the IsAny check once all forms are typed.
  // Currently used to defer fixing untyped FormControl.
  @Input() type: IsAny<T> extends true
    ? any
    : // Accepts `undefined` as a default value, but internally, it
      // should never set `control.value` to `undefined`
      [T] extends [number] | [number | undefined]
      ? InputType.NUMBER | InputType.NUMBER_INCREMENT
      : [T] extends [string]
        ?
            | InputType.CURRENCY
            | InputType.EMAIL
            | InputType.NUMBER_TEXT
            | InputType.PASSWORD
            | InputType.TEXT
            | InputType.SEARCH
        : [T] extends [Date | null]
          ? InputType.DATE
          : [T] extends [DateRange | null | undefined]
            ? InputType.DATERANGE | InputType.DATERANGE_INLINE
            : never;
  @Input() iconStart?: InputIconStart;
  @Input() iconEnd?: InputIconEnd;
  @Input() iconStartLabel?: string;
  @Input() iconEndLabel?: string;
  @Input() leftIconSize = IconSize.MID;
  @Input() leftIconColor = IconColor.INHERIT;
  @Input() rightIconSize = IconSize.MID;
  @Input() rightIconColor = IconColor.INHERIT;
  @Input() beforeLabel = '';
  @Input() afterLabel = '';
  /**
   * Sets aria label for accessibility when there is no associated visible text
   */
  @Input() label?: string;
  @Input() maxLength?: number;
  @Input() maxCharacter?: number;
  @Input() textAlign: InputTextAlign = InputTextAlign.LEFT;
  /**
   * Improves UX for form fields like Usernames, Emails, Company Names, etc. Autocomplete defaults to 'on' but you can disable
   * it with 'off'. If you wish to use more specific tokens like 'given-name' and 'family-name', see the link below.
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/autoComplete#token_list_tokens
   */
  @Input() autoComplete: AutoCompleteHint = 'on';
  @Input() set disabled(value: boolean) {
    this.attrDisabled = value ? true : undefined;
    this.updateIconClickability();
  }

  /**
   * Set how to horizontally align the number increment input
   */
  @Input() align = InputAlign.LEFT;

  /**
   * Force color white for transparent cases in light mode.
   */
  @Input() color = InputTextColor.DEFAULT;

  /**
   * Set a minimum value for number inputs
   */
  @Input() minValue?: number;

  // Allow input autofocus, not just dateInput
  @Input() autofocus = AutoFocusOption.NONE;

  /** First validation happens only on blur, and validates on keyup once an error exists */
  @HostBinding('attr.data-dynamic-validation')
  @Input()
  dynamicValidation?: boolean = false;

  /** Displays hint under input field upon focus */
  @Input() hint?: string;
  /** Highlight input field when date value is set */
  @Input() showDateSelectedState?: boolean = false;
  /** Shake input on blur event */
  @Input() shakeOnBlur = true;
  /** Immediately check state of input with validation */
  @Input() validationStateCheck = false;

  @Input() placeholders: readonly string[] = [];

  @Input() searchRoundCorners = true;

  /** Hints the browser what kind of virtual keyboard to display when the input is focused. */
  @Input() keyboardType?: KeyboardType;
  /**
   * This input only works on mobile when the type is `InputType.DATE`.
   * If true, the date icon will be hidden on mobile.
   */
  @Input() compactDateInput = false;

  /**
   * Minimum selectable date for the datepicker.
   */
  @Input() minDate?: Date;

  /**
   * Maximum selectable date for the datepicker.
   */
  @Input() maxDate?: Date;

  /**
   * If false, the increment buttons will be hidden.
   * Applies to InputType of NUMBER_TEXT and NUMBER.
   */
  @Input() showIncrement = true;

  /**
   * Set the background color of the input component
   */
  @Input() backgroundColor?: InputBackgroundColor = 'default';

  @Output() iconStartClick = new EventEmitter<MouseEvent>();
  @Output() iconEndClick = new EventEmitter<MouseEvent>();
  @Output() onFocus = new EventEmitter<void>();
  @Output() onBlur = new EventEmitter<void>();

  @ViewChild('nativeElement') nativeElement: ElementRef<HTMLInputElement>;
  @ViewChild('nativeElement2') nativeElement2?: ElementRef<HTMLInputElement>;
  @ViewChild('inputContainer', { read: ElementRef })
  inputContainer: ElementRef<HTMLDivElement>;
  @ViewChild('picker') matDatepicker?: MatDatepicker<Date>;
  @ViewChild('inlineRangePicker') inlineRangePicker?: MatCalendar<Date>;

  shakeState = 'void';

  get shouldCenterAlign(): boolean {
    return this.maxLength === 1;
  }

  get controlType(): ControlType {
    switch (this.type) {
      case InputType.NUMBER:
      case InputType.NUMBER_INCREMENT:
        return ControlType.NUMBER;
      case InputType.DATE:
        return ControlType.DATE;
      case InputType.DATERANGE:
      case InputType.DATERANGE_INLINE:
        return ControlType.DATERANGE;
      case InputType.CURRENCY:
      case InputType.NUMBER_TEXT:
      default:
        return ControlType.STRING;
    }
  }

  get isNumberInput(): boolean {
    return [
      InputType.NUMBER,
      InputType.NUMBER_INCREMENT,
      InputType.CURRENCY,
      InputType.NUMBER_TEXT,
    ].includes(this.type);
  }

  get isNumberIncrement(): boolean {
    return this.type === InputType.NUMBER_INCREMENT;
  }

  get isText(): boolean {
    return this.type === InputType.TEXT;
  }

  get inputType(): InputType {
    if (this.type === InputType.PASSWORD) {
      return this.showPassword ? InputType.TEXT : InputType.PASSWORD;
    }

    if (this.isNumberIncrement || this.type === InputType.CURRENCY) {
      return InputType.NUMBER;
    }

    return this.type;
  }

  get hasError(): boolean {
    return this.control.invalid && (this.control.touched || this.control.dirty);
  }

  private autofocusSubject$ = new BehaviorSubject<boolean>(false);
  private afterViewInitSubject$ = new BehaviorSubject<boolean>(false);

  ngOnChanges(changes: SimpleChanges): void {
    if ('id' in changes || 'label' in changes) {
      const id = this.id ?? generateUniqueID();
      const labelId = this.label ? `-${this.label}` : '';
      this.errorId = `errorId-${id}${labelId}`;
    }

    if (this.control !== undefined && this.InputType === undefined) {
      // Throw an error since `InputType` is required to
      // ensure type correctness for `control`
      throw new Error('InputType must be specified.');
    }

    if (
      'type' in changes &&
      changes.type.previousValue !== changes.type.currentValue
    ) {
      this.isDateInput =
        this.type === InputType.DATE || this.type === InputType.DATERANGE;
      this.isPasswordInput = this.type === InputType.PASSWORD;

      this.formatCurrency();
    }

    if ('autofocus' in changes) {
      this.autofocusSubject$.next(this.autofocus !== AutoFocusOption.NONE);
    }

    if ('attrReadonly' in changes || 'disabled' in changes) {
      this.updateIconClickability();
    }

    if ('control' in changes || 'dynamicValidation' in changes) {
      if (this.errorSubscription) {
        this.errorSubscription.unsubscribe();
      }
      this.errorSubscription = this.control.statusChanges.subscribe(() => {
        this.isErrorDisplayed =
          this.control.invalid && (this.control.touched || this.control.dirty);

        if (
          this.control.invalid &&
          (this.control.touched || this.control.dirty) &&
          this.control.value !== '' &&
          isDefined(this.control.value)
        ) {
          this.validationStateCheck = true;
        }

        if (this.validationStateCheck && this.control.valid) {
          this.isValid = true;
        }

        if (this.validationStateCheck && this.control.invalid) {
          this.isValid = false;
          if (
            this.hasError &&
            !this.isWarning &&
            this.dynamicValidation &&
            this.shakeOnBlur
          ) {
            this.shakeState = 'active';
          }
        }

        this.cd.markForCheck();
      });

      // `statusChanges` does not emit on (re)initialization, so seed the flag from
      // the current state, else an already-invalid control lets the hint overlap the error.
      this.isErrorDisplayed = this.hasError;

      // This sets the control's updateOn value to `blur` if on dynamicValidation
      if (this.control && this.dynamicValidation) {
        this.control = Object.defineProperty(this.control, 'updateOn', {
          get: () => 'blur',
          configurable: true,
        });
      }
    }

    if ('size' in changes) {
      this.updateButtonIncrementSize();
    }

    if ('clearDateRange' in changes) {
      if (this.type === InputType.DATERANGE_INLINE && this.clearDateRange) {
        this.clearInlineDateRange();
      }
    }

    if ('placeholders' in changes && this.placeholders.length > 0) {
      if (isPlatformBrowser(this.platformId)) {
        this.placeholder = '';
        this.typePlaceholder(false);
      } else {
        [this.placeholder] = this.placeholders;
      }
    }
  }

  ngAfterViewInit(): void {
    this.afterViewInitSubject$.next(true);
    this.updateCharCount();
  }

  ngOnInit(): void {
    this.dateFormat = this.datefns.dateFormat;

    // We are able to check for subscriptions to these inputs in order to infer
    // whether or not the icons should be clickable (displayed as buttons)
    this.iconStartClickable = this.iconStartClick.observed;
    this.updateIconClickability();

    if (this.isDateInput) {
      // force a calendar picker icon
      this.iconEnd = 'ui-calendar-v2';
      this.iconEndLabel = this.iconEndLabel ?? 'ui-calendar-v2';
      this.updateIconClickability();

      if (this.type === InputType.DATERANGE) {
        this.dateRangeGroup = new FormGroup({
          dateStartControl: new FormControl<Date | undefined>(undefined, {
            nonNullable: true,
          }),
          dateEndControl: new FormControl<Date | undefined>(undefined, {
            nonNullable: true,
          }),
        });

        // propagate manual changes on the combined public control to the internal daterange controls
        this.dateRangeControlSubscription = this.control.valueChanges
          .pipe(startWith(this.control.value))
          .subscribe(value => {
            // Despite the `emitEvent: false` option being supplied, all of these events are
            // still trigger the dateRangeGroup valueChanges subscription ¯\_(ツ)_/¯
            if (!value) {
              this.dateRangeGroup?.reset(undefined, {
                emitEvent: false,
                onlySelf: true,
              });
              return;
            }

            // Not in the next if block due to prettier error
            if (!(value instanceof MatDateRange)) {
              return;
            }

            if (
              !(
                (value.start === null || value.start instanceof Date) &&
                (value.end === null || value.end instanceof Date)
              )
            ) {
              return;
            }

            if (
              value.start !==
              this.dateRangeGroup?.controls.dateStartControl.value
            ) {
              this.dateRangeGroup?.controls.dateStartControl.setValue(
                value.start,
                { emitEvent: false, onlySelf: true },
              );
            }

            if (
              value.end !== this.dateRangeGroup?.controls.dateEndControl.value
            ) {
              this.dateRangeGroup?.controls.dateEndControl.setValue(value.end, {
                emitEvent: false,
                onlySelf: true,
              });
            }
          });

        // propagate changes on the internal daterange controls to the combined public control
        this.dateRangeSubscription = this.dateRangeGroup.valueChanges.subscribe(
          value => {
            if (
              this.type === InputType.DATERANGE &&
              this.isDateRangeControl(this.control) &&
              isDefined(value.dateStartControl) &&
              isDefined(value.dateEndControl)
            ) {
              this.control.markAsTouched();
              this.control.markAsDirty();

              this.control.setValue(
                new MatDateRange(value.dateStartControl, value.dateEndControl),
              );

              // set errors on control if dateRangeGroup controls are invalid
              if (this.dateRangeGroup?.controls.dateStartControl.errors) {
                this.control.setErrors(
                  this.dateRangeGroup.controls.dateStartControl.errors,
                );
              } else if (this.dateRangeGroup?.controls.dateEndControl.errors) {
                this.control.setErrors(
                  this.dateRangeGroup.controls.dateEndControl.errors,
                );
              }
            }
          },
        );
      }
    }

    if (this.isPasswordInput) {
      this.iconEnd = 'ui-visibility-off';
      this.updateIconClickability();
    }

    this.updateButtonIncrementSize();

    this.autofocusSubscription = combineLatest([
      this.autofocusSubject$.asObservable(),
      this.afterViewInitSubject$.asObservable(),
    ])
      .pipe(filter(([autofocus, afterViewInit]) => afterViewInit && autofocus))
      .subscribe(() => {
        if (!this.isDateInput) {
          this.handleAutoFocus();
        }
      });
  }

  // Match button size with input size
  private updateButtonIncrementSize(): undefined {
    if (!this.isNumberIncrement) {
      return;
    }

    switch (this.size) {
      case InputSize.SMALL:
        this.buttonIncrementSize = ButtonSize.SMALL;
        break;
      case InputSize.MID:
        this.buttonIncrementSize = ButtonSize.MID;
        break;
      case InputSize.LARGE:
        this.buttonIncrementSize = ButtonSize.LARGE;
        break;
      default:
        return undefined;
    }
  }

  ngOnDestroy(): void {
    if (this.errorSubscription) {
      this.errorSubscription.unsubscribe();
    }

    if (this.autofocusSubscription) {
      this.autofocusSubscription.unsubscribe();
    }

    if (this.dateRangeControlSubscription) {
      this.dateRangeControlSubscription.unsubscribe();
    }

    if (this.dateRangeSubscription) {
      this.dateRangeSubscription.unsubscribe();
    }

    if (this.placeholderAnimationTimeout) {
      clearTimeout(this.placeholderAnimationTimeout);
    }
  }

  handleIconEndClick(e: MouseEvent): void {
    if (this.isDateInput) {
      // open the matDatePicker instead of emitting
      // it automatically closes when you click outside
      this.matDatepicker?.open();
      return;
    }

    if (this.isPasswordInput) {
      this.showPassword = !this.showPassword;

      if (this.showPassword) {
        this.iconEnd = 'ui-visibility';
      } else {
        this.iconEnd = 'ui-visibility-off';
      }
      return;
    }
    this.iconEndClick.emit(e);
  }

  handleAutoFocus(): void {
    if (!this.inputFocused) {
      if (this.autofocus !== AutoFocusOption.NONE) {
        this.focus.focusElement(this.nativeElement, {
          allowMobile: this.autofocus === AutoFocusOption.ALWAYS,
        });
        this.inputFocused = true;
      }
    }
  }

  handleFocusDatePicker(element: string): void {
    if (
      !this.inputFocused &&
      !this.matDatepicker?.opened &&
      !this.testingDatepickerOpenCloseDisabled &&
      !this.attrDisabled &&
      !this.attrReadonly
    ) {
      this.matDatepicker?.open();
      // timeout to retain focus on input field after datepicker opens
      this.timeUtils.setTimeout(() => {
        if (element === 'nativeElement') {
          this.focus.focusElement(this.nativeElement, {
            allowMobile: this.autofocus === AutoFocusOption.ALWAYS,
          });
        } else if (this.nativeElement2) {
          this.focus.focusElement(this.nativeElement2, {
            allowMobile: this.autofocus === AutoFocusOption.ALWAYS,
          });
        }

        this.inputFocused = true;
        this.cd.markForCheck();
      });
    }
  }

  handleBlurDatePicker(): void {
    // Timeout to avoid re-triggering the focus conditions as
    // clicking the input causes the datepicker to open, which invokes a blur event
    // before it is re-focused programatically inside handleFocusDatePicker().
    this.timeUtils.setTimeout(() => {
      // Check if the input is still focused after the timeout to avoid incorrectly
      // overriding the inputFocused state.
      this.inputFocused =
        document.activeElement === this.nativeElement.nativeElement ||
        document.activeElement === this.nativeElement2?.nativeElement;
      this.cd.markForCheck();
    }, 200);
  }

  handleKeydown(): void {
    if (this.matDatepicker?.opened) {
      this.matDatepicker?.close();
    }
  }

  handleIconStartClick(e: MouseEvent): void {
    this.iconStartClick.emit(e);
  }

  // Prevents "scrolling" inside the input type number
  handleInputWheel(): void {
    if (
      (this.type === InputType.NUMBER || this.type === InputType.CURRENCY) &&
      this.inputFocused
    ) {
      this.nativeElement.nativeElement.blur();
    }
  }

  formatCurrency(): void {
    if (
      this.type === InputType.CURRENCY &&
      this.isStringControl(this.control) &&
      this.control.value
    ) {
      const currentVal: string = this.control.value;
      const currentValNum = parseFloat(currentVal);
      this.control.setValue(currentValNum.toFixed(2));
    }
  }

  handleFocus(): void {
    this.onFocus.emit();
    this.inputFocused = true;

    if (this.dynamicValidation && this.hasError) {
      this.isWarning = true;
    }
  }

  handleBlur(): void {
    if (this.dynamicValidation) {
      this.validateField();
      this.isWarning = false;
    }

    this.onBlur.emit();
    this.inputFocused = false;
    this.formatCurrency();

    if (
      this.control.invalid &&
      (this.control.touched || this.control.dirty) &&
      this.shakeOnBlur
    ) {
      this.shakeState = 'active';
    }
  }

  handleOnChange(): void {
    if (this.type === InputType.CURRENCY || this.type === InputType.NUMBER) {
      this.handleDynamicValidation();
    }
  }

  handleKeyUp(): void {
    this.updateCharCount();
    this.handleDynamicValidation();
  }

  handleDynamicValidation(): void {
    // We want to only keep validating if there is already an existing error
    if (!this.dynamicValidation || !this.hasError) {
      return;
    }

    this.validateField();
  }

  shakeDone(): void {
    this.shakeState = 'void';
  }

  handleAdd(): void {
    if (
      (this.isNumberIncrement || this.isNumberInput) &&
      (this.isNumberControl(this.control) || this.isStringControl(this.control))
    ) {
      let currentVal = 0;
      if (this.isNumberControl(this.control)) {
        currentVal = Number.isNaN(currentVal) ? 0 : this.control.value;
        this.control.setValue(
          currentVal + (this.type === InputType.CURRENCY ? 0.01 : 1),
        );
      } else {
        currentVal = this.control.value
          ? parseFloat(this.control.value as string)
          : 0;
        if (this.type === InputType.CURRENCY) {
          this.control.setValue(
            parseFloat((currentVal + 0.01).toFixed(2)).toString(),
          );
        } else {
          this.control.setValue((currentVal + 1).toString());
        }
      }

      this.control.markAsTouched();
    }
  }

  handleMinus(): void {
    if (
      (this.isNumberIncrement || this.isNumberInput) &&
      (this.isNumberControl(this.control) || this.isStringControl(this.control))
    ) {
      let currentVal = 0;
      if (this.isNumberControl(this.control)) {
        currentVal = Number.isNaN(currentVal) ? 0 : this.control.value;

        if (isDefined(this.minValue) && this.minValue >= currentVal) {
          return;
        }
        this.control.setValue(
          currentVal - (this.type === InputType.CURRENCY ? 0.01 : 1),
        );
      } else {
        currentVal = this.control.value
          ? parseFloat(this.control.value as string)
          : 0;

        if (isDefined(this.minValue) && this.minValue >= currentVal) {
          return;
        }
        if (this.type === InputType.CURRENCY) {
          this.control.setValue(
            parseFloat((currentVal - 0.01).toFixed(2)).toString(),
          );
        } else {
          this.control.setValue((currentVal - 1).toString());
        }
      }

      this.control.markAsTouched();
    }
  }

  handleInlineDateRangeChange(date: Date): void {
    // material datepicker's inline calendar doesn't have a built-in control
    // and doesn't supply a DateRange type so we have to set it manually
    if (!date) {
      // date is only null when date control is cleared manually
      return;
    }

    if (
      this.inlineDateRangeValue &&
      this.inlineDateRangeValue.start &&
      !this.inlineDateRangeValue.end &&
      date >= this.inlineDateRangeValue.start
    ) {
      // start selected, end not selected and selected date is after start date
      this.inlineDateRangeValue = new MatDateRange(
        this.inlineDateRangeValue.start,
        date,
      );

      if (this.isDateRangeControl(this.control)) {
        this.control.setValue(this.inlineDateRangeValue);
      }
    } else {
      // neither or both selected or selected date is before start date
      this.inlineDateRangeValue = new MatDateRange(date, null);
    }
  }

  validateField(): void {
    const nativeElementValue = this.nativeElement.nativeElement.value;

    // Handle number and string, Date is not validated
    if (this.isNumberControl(this.control)) {
      this.control.setValue(parseFloat(nativeElementValue));
    } else if (this.isStringControl(this.control)) {
      this.control.setValue(nativeElementValue);
    }

    if (this.nativeElement.nativeElement.value !== '') {
      dirtyAndValidate(this.control);
    } else if (this.isNumberControl(this.control)) {
      this.control.reset(NaN);
    } else if (this.isStringControl(this.control)) {
      this.control.reset('');
    }
  }

  // We have to manually update the max character counter instead of getting
  // control.value.length because updateOn is 'blur' when dynamic validation is used
  updateCharCount(): void {
    if (this.maxCharacter) {
      this.remainingCharCount =
        this.maxCharacter - this.nativeElement.nativeElement.value.length;
    }
  }

  clearInlineDateRange(): void {
    if (this.inlineRangePicker) {
      this.inlineDateRangeValue = undefined;
      this.inlineRangePicker.selectedChange.emit(undefined);
    }
  }

  shouldDisableDecrementButton(): boolean {
    if (this.isNumberIncrement && this.isNumberControl(this.control)) {
      let currentVal: number = this.control.value;

      currentVal = Number.isNaN(currentVal) ? 0 : currentVal;
      return isDefined(this.minValue) && this.minValue >= currentVal;
    }

    return false;
  }

  updateIconClickability(): void {
    this.iconEndClickable =
      !this.attrDisabled &&
      !this.attrReadonly &&
      (this.isDateInput || this.isPasswordInput || this.iconEndClick.observed);
  }

  isStringControl(control: AbstractControl): control is FormControl<string> {
    return this.controlType === ControlType.STRING;
  }

  isNumberControl(control: AbstractControl): control is FormControl<number> {
    return this.controlType === ControlType.NUMBER;
  }

  isDateControl(
    control: AbstractControl,
  ): control is FormControl<Date | undefined> {
    return this.controlType === ControlType.DATE;
  }

  isDateRangeControl(
    control: AbstractControl,
  ): control is FormControl<DateRange | undefined> {
    return this.controlType === ControlType.DATERANGE;
  }

  typePlaceholder(isDeletingText: boolean): void {
    const currentFullPlaceholderText =
      this.placeholders[this.currentAnimatedPlaceholderIndex];
    const words = currentFullPlaceholderText.split(' ');

    this.placeholder = words
      .slice(
        0,
        this.placeholder
          ? this.placeholder.split(' ').length + (isDeletingText ? -1 : 1)
          : 1,
      )
      .join(' ');

    let _isDeletingText = isDeletingText;
    if (this.placeholder === currentFullPlaceholderText && !isDeletingText) {
      _isDeletingText = true;
    } else if (this.placeholder === '' && isDeletingText) {
      _isDeletingText = false;
      this.currentAnimatedPlaceholderIndex =
        (this.currentAnimatedPlaceholderIndex + 1) % this.placeholders.length;
    }

    this.cd.markForCheck();

    this.placeholderAnimationTimeout = this.timeUtils.setTimeout(
      () => this.typePlaceholder(_isDeletingText),
      this.placeholder === currentFullPlaceholderText
        ? this.PLACEHOLDER_ANIMATION_FULL_TEXT_DELAY
        : this.PLACEHOLDER_ANIMATION_TYPING_DELAY,
    );
  }

  getPlaceholderWithCursor(): string | undefined {
    return this.placeholders?.length > 0 &&
      this.isStringControl(this.control) &&
      this.control.value.length === 0
      ? `${this.placeholder}|`
      : this.placeholder;
  }
}
